import { ClipboardCheck, Sparkles } from "lucide-react";
import { SEVERITY_META, SEVERITY_TIERS } from "@taskiq/shared";
import { Badge, SeverityChip } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/Feedback";
import { PageHeader } from "@/components/ui/PageHeader";
import { SecondScale } from "@/components/marketing/SecondScale";

/**
 * Placeholder for the assessment feed.
 *
 * It renders two things for real rather than faking a list. The severity legend
 * comes from the shared constants, because that vocabulary is fixed now and both
 * clients have to agree on it - getting it on screen before the feed exists means
 * the hazard list drops into a page whose language is already settled rather than
 * one being designed under deadline.
 *
 * The ninety-second breakdown is the same component the marketing site's hero is
 * built on, driven by the same `NFR_TARGETS`. An administrator asking "what am I
 * actually asking my crews to do" gets the answer here rather than on a sales
 * page they will never revisit.
 *
 * ── It says on the page that it is not built ────────────────────────────────
 *
 * It did not, and an empty state alone cannot carry that: "No assessments yet"
 * is a true sentence about a working feed with nothing in it, and it is exactly
 * what an administrator would see the day before their crews start. Somebody
 * reading it had no way to tell whether they were waiting on their technicians
 * or on us. The badge and the band say which, and `Reports` says it the same
 * way - see `.preview-note`.
 */
export function Assessments() {
  return (
    <div className="page">
      <PageHeader
        title="Assessments"
        badge={<Badge tone="accent">Coming soon</Badge>}
        description="Pre-task records from the field, with their findings and media."
      />

      <Card>
        <EmptyState
          icon={<ClipboardCheck />}
          title="No assessments yet"
          description="Once technicians start running pre-task checks in the TaskIQ app, every completed assessment appears here - scoped to the locations you can reach."
        />
      </Card>

      <div className="preview-grid">
        <Card>
          <CardHeader
            title="Severity tiers"
            subtitle="How findings are ranked, and what each tier asks of the technician"
          />
          <CardBody>
            <ul className="vocab-list">
              {SEVERITY_TIERS.map((tier) => (
                <li key={tier} className="vocab-list__item">
                  <div className="severity-list__chip">
                    <SeverityChip severity={tier} />
                  </div>
                  <p className="muted">{SEVERITY_META[tier].description}</p>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="What a technician does"
            subtitle="The sequence behind each record, and where the ninety seconds go"
          />
          <CardBody>
            {/* The list of phase names and seconds that used to sit under this
                is gone: the scale prints both now, and the same five rows twice
                on one card read as the card not trusting its own diagram. */}
            <SecondScale tone="surface" />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
